'use client'

import { GAMES, VISUAL_LAB_DECOR_MODELS } from '@/constants/GAMES'
import { markerFromSearchParams } from '@/constants/MARKERS'
import { GlbModelsProvider } from '@/contexts/glb-models-context'
import { AbstractModel } from '@/scene/AbstractModel'
import { usePathname, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useMemo, useState, type ReactNode } from 'react'
import { preloadGameAssets } from '@/lib/model-preload'
import type { GameId } from '@/constants/GAMES'
import { normalizeAmbientFishConfigs } from '@/scene/SceneProps'

export const GAME_DETECTED_EVENT = 'mosquarium:game-detected'
export const GAME_HINT_RESET_EVENT = 'mosquarium:game-hint-reset'

export type GameDetectedEventDetail = {
  gameId: GameId
}

function uniqueModels(models: AbstractModel[]): AbstractModel[] {
  return Array.from(new Map(models.map(model => [model.key, model])).values())
}

function normalizePathname(pathname: string) {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
}

function glbModelsForRoute(pathname: string, gameId: string | null): AbstractModel[] {
  const hintedGame = gameId ? GAMES[gameId] : null

  const allActiveGameModels = Object.values(GAMES).flatMap(game => [
    game.weaponModel,
    ...game.garbageModels,
    ...(game.environmentDecor?.map(item => item.model) ?? []),
    ...normalizeAmbientFishConfigs(game.ambientFish).flatMap(cfg => cfg.models.map(fish => fish.model)),
  ])
  if (pathname === '/web') {
    return uniqueModels(allActiveGameModels)
  }

  if (pathname === '/visual-lab') {
    return uniqueModels([...allActiveGameModels, ...VISUAL_LAB_DECOR_MODELS])
  }

  if (pathname.endsWith('/scan') || pathname.endsWith('/game')) {
    if (hintedGame) {
      return uniqueModels([
        hintedGame.weaponModel,
        ...hintedGame.garbageModels,
        ...(hintedGame.environmentDecor?.map(item => item.model) ?? []),
        ...normalizeAmbientFishConfigs(hintedGame.ambientFish).flatMap(cfg => cfg.models.map(fish => fish.model)),
      ])
    }
    return []
  }

  return []
}

function GlbModelsFromRouteInner({ children }: { children: ReactNode }) {
  const searchParams = useSearchParams()
  const pathname = normalizePathname(usePathname())
  const searchParamString = searchParams.toString()
  const hintedMarker = useMemo(
    () => markerFromSearchParams(new URLSearchParams(searchParamString)),
    [searchParamString]
  )
  const hintedGameId = hintedMarker?.gameId ?? null
  const [runtimeGameId, setRuntimeGameId] = useState<GameId | null | undefined>(undefined)
  const selectedGameId = runtimeGameId === undefined ? hintedGameId : runtimeGameId
  const models = useMemo(() => glbModelsForRoute(pathname, selectedGameId), [pathname, selectedGameId])

  useEffect(() => {
    setRuntimeGameId(undefined)
  }, [hintedGameId, pathname])

  useEffect(() => {
    const handleDetected = (event: Event) => {
      const { gameId } = (event as CustomEvent<GameDetectedEventDetail>).detail ?? {}
      if (gameId && gameId in GAMES) {
        setRuntimeGameId(gameId)
      }
    }

    const handleReset = () => setRuntimeGameId(null)

    window.addEventListener(GAME_DETECTED_EVENT, handleDetected)
    window.addEventListener(GAME_HINT_RESET_EVENT, handleReset)
    return () => {
      window.removeEventListener(GAME_DETECTED_EVENT, handleDetected)
      window.removeEventListener(GAME_HINT_RESET_EVENT, handleReset)
    }
  }, [])

  useEffect(() => {
    // На /scan?marker=X URL — только preload hint. Не выбираем игру по URL,
    // но можем заранее качать модели, пока пользователь наводит камеру.
    if (pathname.endsWith('/scan') && hintedGameId) {
      preloadGameAssets(hintedGameId)
    }
  }, [hintedGameId, pathname])

  return <GlbModelsProvider models={models}>{children}</GlbModelsProvider>
}

/**
 * GLB больше не грузятся на уровне всего сайта. Provider получает пустой список
 * для /, /onboarding и /scan без hint. QR-flow догружает GLB по `?marker=`
 * или после реального распознавания QR через runtime event.
 */
export function GlbModelsFromRoute({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<GlbModelsProvider models={[]}>{children}</GlbModelsProvider>}>
      <GlbModelsFromRouteInner>{children}</GlbModelsFromRouteInner>
    </Suspense>
  )
}
